import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner } from "reactstrap";
import PaymentMethodsCard from "./Payment/PaymentMethodsCard";
import lock from "assets/img/lock.svg";
import PayPalPayment from "./Payment/PayPalPayment";
import StripePayment from "./Payment/StripePayment";
import { usePaymentSDKValidation } from "hooks/usePaymentSDKValidation";
import { useDispatch, useSelector } from "react-redux";
import { useHolidayGuard } from "hooks/useHolidayGuard";
import { addPaymentAction } from "store/actions/orderActions";
import { getRestaurantsAction } from "store/actions/restaurantAction";

const Payment = ({
  setShowPayment,
  totalAmount,
  confirmOrder,
  loading,
  showBackButton = true,
}) => {
  const dispatch = useDispatch();
  const [paymentMethod, setPaymentMethod] = useState("");
  const { restaurantsDetail } = useSelector((state) => state.restaurant);
  const { user } = useSelector((state) => state.auth);

  const currency = restaurantsDetail?.currency;
  const [isHoliday, setIsHoliday] = useState({
    isHoliday: false,
    title: "",
    storeTiming: false,
    type: "",
  });

  const { holidayStatus, checkAndToast } = useHolidayGuard(
    restaurantsDetail,
    "dinein"
  );
  const { paypalSdkValid, stripeSdkValid } =
    usePaymentSDKValidation(restaurantsDetail);

  const paymentOptions = [
    {
      methodName: "cash",
      displayName: "Pay With Cash",
    },
  ];

  if (
    (paypalSdkValid &&
      restaurantsDetail?.paymentConnections?.paypal &&
      restaurantsDetail?.paypalClientKey) ||
    (stripeSdkValid &&
      restaurantsDetail?.paymentConnections?.stripe &&
      restaurantsDetail?.stripeClientKey)
  ) {
    paymentOptions.push({
      methodName: "card",
      displayName: "Pay With Card",
    });
  }

  const calculateFeeDetails = (forwardFeesObj, type, totalAmount) => {
    const forwardFees = forwardFeesObj?.find((val) => val?.provider === type);

    let feesAmount = 0;
    let finalAmount = totalAmount;
    let feesType = "";

    if (!forwardFees) {
      return { feesAmount, feesType, finalAmount };
    }

    feesType = forwardFees?.type;

    if (forwardFees?.type === "fixed_price") {
      feesAmount = forwardFees?.fixedAmount;
      finalAmount = totalAmount + parseFloat(forwardFees?.fixedAmount);
    } else if (forwardFees?.type === "percentage") {
      const percentage = parseFloat(forwardFees?.percentageAmount) / 100;
      feesAmount = parseFloat(forwardFees.percentageAmount);
      finalAmount = totalAmount + totalAmount * percentage;
    } else if (forwardFees?.type === "both") {
      feesAmount = `${parseFloat(forwardFees?.percentageAmount)}% + ${
        forwardFees?.fixedAmount
      }`;

      let percentage = parseFloat(forwardFees?.percentageAmount) / 100;
      finalAmount =
        totalAmount +
        parseFloat(forwardFees?.fixedAmount) +
        totalAmount * percentage;
    }

    return { feesAmount, feesType, finalAmount };
  };

  const forwardFeesObj = restaurantsDetail?.forwardFees;
  const {
    feesAmount: paypalFees,
    feesType: paypalFeesType,
    finalAmount: paypalAmount,
  } = calculateFeeDetails(forwardFeesObj, "paypal", totalAmount);

  const {
    feesAmount: stripeFees,
    feesType: stripeFeesType,
    finalAmount: stripeAmount,
  } = calculateFeeDetails(forwardFeesObj, "stripe", totalAmount);

  const handleCashPayment = async () => {
    if (checkAndToast()) return;

    const orderID = await confirmOrder("cash", true);

    if (orderID) {
      const paymentObject = {
        orderId: orderID,
        paymentMethod: "cash",
        amount: (totalAmount || 0).toString(),
        status: "status",
        uid: user?.id,
      };

      dispatch(addPaymentAction(paymentObject));
    }
  };

  useEffect(() => {
    dispatch(getRestaurantsAction(user?.restaurantID));
  }, []);

  return (
    <Container fluid className="pt-2 pb-4">
      <Row className="d-flex justify-content-start align-items-center px-0 py-2 mx-0">
        <Col xs="2" className="d-flex justify-content-start px-0">
          {showBackButton && (
            <Button
              size="sm"
              color="primary"
              disabled={loading}
              onClick={() => setShowPayment(false)}
            >
              <i className="fas fa-arrow-left "></i>
            </Button>
          )}
        </Col>
        <Col xs="8" className="d-flex justify-content-center px-0">
          <h4 className="fs--25 fw--600 mb-0 normal-heading">Payment</h4>
        </Col>
        <Col xs="2" className="px-0">
          {/* Optional spacing or empty */}
        </Col>
      </Row>
      <Row className="d-flex justify-content-center px-0 mx-0">
        <Col className="col-12 d-flex justify-content-center py-3">
          <h4 className="fs--16 fw--600 mb-0 normal-heading text-grey">
            Choose your Payment Method
          </h4>
        </Col>
      </Row>
      <Row>
        <Col className="my-2 pb-3 d-flex flex-column align-items-center justify-content-start">
          <PaymentMethodsCard
            paymentOptions={paymentOptions}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
          />

          {paymentMethod === "card" && (
            <>
              {paypalSdkValid && restaurantsDetail && (
                <>
                  <PayPalPayment
                    totalAmount={totalAmount}
                    currency={currency}
                    restaurantsDetail={restaurantsDetail}
                    isHoliday={isHoliday}
                    paypalClientKey={restaurantsDetail?.paypalClientKey}
                    paypalFees={paypalFees}
                    paypalFeesType={paypalFeesType}
                    paypalAmount={paypalAmount}
                    confirmOrder={confirmOrder}
                    // location={cartDetails}
                    // getPaypalDetail={getPaypalDetail}
                  />
                  <h4
                    className="fs--21"
                    style={{ color: "rgb(108, 117, 125)" }}
                  >
                    OR
                  </h4>
                </>
              )}

              {stripeSdkValid && restaurantsDetail && (
                <StripePayment
                  totalAmount={totalAmount}
                  currency={currency}
                  restaurantsDetail={restaurantsDetail}
                  isHoliday={isHoliday}
                  stripeClientKey={restaurantsDetail?.stripeClientKey}
                  stripeFees={stripeFees}
                  stripeFeesType={stripeFeesType}
                  stripeAmount={stripeAmount}
                  confirmOrder={confirmOrder}
                  // location={cartDetails}
                />
              )}
            </>
          )}

          <Row className="d-flex justify-content-center align-items-center px-3">
            <img alt="security" src={lock} className="mx-2" />
            <h6 className="fs--16 fw--400 mb-0 text-muted">
              We protect your payment information using encryption to provide
              bank-level security.
            </h6>
          </Row>
        </Col>
      </Row>
      {paymentMethod == "cash" && (
        <Row className="d-flex justify-content-center p-3">
          <Button
            color="none"
            className="confirm-btn d-flex justify-content-center align-items-center fs--16 fw--400 my-2 text-white"
            style={{
              minHeight: "46px",
              minWidth: "330px",
              backgroundColor: restaurantsDetail?.adminColor,
            }}
            onClick={handleCashPayment}
            disabled={loading}
          >
            {loading ? (
              <Spinner size="sm" color="white" />
            ) : (
              `Confirm order for ${currency || "$"} ${totalAmount}`
            )}
          </Button>
        </Row>
      )}
    </Container>
  );
};

export default Payment;
